import { useAuth0 } from "@auth0/auth0-react";
import { useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DEFAULT_LOCALE, getDirection, getHtmlLang } from "../../lib/i18n";
import { userStatusQueryOptions } from "../../lib/userQueries";

interface LanguageContextType {
	userLanguage: string;
	isLoading: boolean;
}

const LanguageContext = createContext<LanguageContextType>({
	userLanguage: DEFAULT_LOCALE,
	isLoading: true,
});

export function useLanguageContext() {
	return useContext(LanguageContext);
}

export function LanguageProvider({ children }: { children: ReactNode }) {
	const [userLanguage, setUserLanguage] = useState(DEFAULT_LOCALE);
	const [isLoading, setIsLoading] = useState(true);
	const { i18n } = useTranslation();
	const { user, isAuthenticated, isLoading: auth0Loading } = useAuth0();
	const queryClient = useQueryClient();

	useEffect(() => {
		const safetyTimeout = setTimeout(() => {
			console.warn(
				"LanguageProvider safety timeout triggered, forcing loading to false",
			);
			setIsLoading(false);
		}, 10000);

		return () => clearTimeout(safetyTimeout);
	}, []);

	useEffect(() => {
		const fetchLanguage = async () => {
			// Wait for Auth0 to finish loading before making API calls
			if (auth0Loading) {
				return;
			}

			// Only fetch user language if authenticated
			if (!isAuthenticated) {
				setIsLoading(false);
				return;
			}

			// Wait for the user id before fetching so status isn't cached under the
			// non-user-scoped ['user','status',undefined] key. The effect re-runs
			// when `user` populates (it's in the dep array).
			if (!user?.sub) {
				return;
			}

			try {
				// Shared cache: dedupes with useUserType so /api/user/status is
				// fetched once for the session.
				const status = await queryClient.ensureQueryData(
					userStatusQueryOptions(user.sub),
				);

				if (status?.display_language) {
					const lang = status.display_language;
					setUserLanguage(lang);
					await i18n.changeLanguage(lang);
				}
			} catch (error) {
				console.error("Error fetching display language:", error);
			} finally {
				setIsLoading(false);
			}
		};

		fetchLanguage();
	}, [i18n, auth0Loading, isAuthenticated, user, queryClient]);

	useEffect(() => {
		const currentLang = i18n.language || DEFAULT_LOCALE;
		const direction = getDirection(currentLang);
		const htmlLang = getHtmlLang(currentLang);

		document.documentElement.setAttribute("dir", direction);
		document.documentElement.setAttribute("lang", htmlLang);
	}, [i18n.language]);

	return (
		<LanguageContext.Provider value={{ userLanguage, isLoading }}>
			{children}
		</LanguageContext.Provider>
	);
}
